// === React & React Hooks ===
import React, { memo, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

// === UI Components ===
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { Icon } from '@iconify/react';

// === Types & Interfaces ===
/**
 * @typedef {Object} UploadFile
 * @property {FormData} file - The file to be uploaded
 * @property {React.Dispatch<React.SetStateAction<string>>} setError - Function to set error state
 * @property {React.Dispatch<React.SetStateAction<number>>} setProgress - Function to set progress state
 */
interface UploadFile {
    file: FormData;
    setError: React.Dispatch<React.SetStateAction<string>>;
    setProgress: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * @typedef {Object} UploadModalProps
 * @property {string} uploadItemName - Name of the item being uploaded
 * @property {boolean} show - Whether the modal is visible
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setShow - Function to set modal visibility
 * @property {string[]} allowedFileTypes - Accepted file formats
 * @property {(file: UploadFile) => Promise<{ hasError: boolean }>} onUpload - Function to handle file upload
 * @property {() => void} onSuccess - Function to call on successful upload
 * @property {number} [maxSize=25] - Maximum file size in MB
 * @property {string} [error] - Error message
 */
interface UploadModalProps {
    uploadItemName: string;
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    allowedFileTypes: string[];
    onUpload: (file: UploadFile) => Promise<{ hasError: boolean }>;
    maxSize?: number;
    error?: string;
}

/**
 * UploadModal component for handling file uploads
 * @param {UploadModalProps} props - Props for the UploadModal component
 * @returns {JSX.Element} The UploadModal component
 */
export const UploadModal = memo(
    ({
        uploadItemName,
        show,
        setShow,
        allowedFileTypes,
        onUpload,
        maxSize = 25,
    }: UploadModalProps) => {
        // State management
        const [file, setFile] = useState<File | null>(null);
        const [fileName, setFileName] = useState<string>('');
        const [fileSize, setFileSize] = useState<string>('');
        const [showFileSizeError, setShowFileSizeError] = useState<boolean>(false);
        const [showFileTypeError, setShowFileTypeError] = useState<boolean>(false);
        const [error, setError] = useState<string>('');
        const [isUploading, setIsUploading] = useState<boolean>(false);
        const [progress, setProgress] = useState<number>(0);

        // MIME types mapping
        const MIME_TYPES: Record<string, string> = {
            jpg: 'image/jpeg',
            pdf: 'application/pdf',
            doc: 'application/msword',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            csv: 'text/csv',
            xls: 'application/vnd.ms-excel',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            txt: 'text/plain',
            md: 'text/markdown',
            json: 'application/json',
            ppt: 'application/vnd.ms-powerpoint',
            pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        };

        // Accepted file types based on allowedFileTypes prop
        const ACCEPTED_FILE_TYPES = allowedFileTypes.reduce(
            (acc, ext) => {
                const mimeType = MIME_TYPES[ext.toLowerCase()];
                if (mimeType) {
                    acc[ext.toUpperCase()] = mimeType;
                }
                return acc;
            },
            {} as Record<string, string>,
        );

        // Error messages
        const ERROR_MESSAGES = {
            FILE_TYPE: `The file type is not supported. Please upload a file in ${allowedFileTypes.join(', ')} format.`,
            FILE_SIZE: `The file size exceeds the maximum limit of ${maxSize}MB.`,
        };

        /**
         * Handles file upload logic
         * @param {File[]} acceptedFiles - Array of accepted files
         */
        const handleFileUpload = (acceptedFiles: File[]): void => {
            resetState();
            if (acceptedFiles.length === 0) setShowFileTypeError(true);
            acceptedFiles.forEach((file) => {
                const reader = new FileReader();

                setFileName(file.name);
                const fileSizeInMB = file.size / 1000000;
                if (fileSizeInMB > maxSize) {
                    setShowFileSizeError(true);
                } else if (!Object.values(ACCEPTED_FILE_TYPES).includes(file.type)) {
                    setShowFileTypeError(true);
                } else {
                    reader.readAsArrayBuffer(file);
                    let fileSize: string;
                    if (fileSizeInMB >= 1) {
                        fileSize = `${fileSizeInMB.toFixed(2)} MB`;
                    } else {
                        fileSize = `${(fileSizeInMB * 1000).toFixed(2)} KB`;
                    }
                    setFileName(file.name);
                    setFileSize(fileSize);
                    setFile(file);
                }
            });
        };

        /**
         * Hides the modal and resets state
         */
        const onHide = useCallback((): void => {
            resetState();
            setShow(false);
        }, [setShow]);

        /**
         * Handles form submission for file upload
         */
        const handleSubmit = useCallback(async (): Promise<void> => {
            if (file) {
                setProgress(0);
                setIsUploading(true);
                try {
                    const formData = new FormData();
                    formData.append(uploadItemName, file);

                    const response = await onUpload({ file: formData, setError, setProgress });
                    if (!response.hasError) {
                        onHide();
                    }
                } catch (error: unknown) {
                    setIsUploading(false);
                    setProgress(0);
                    setError(
                        error instanceof Error ? error.message : 'An unexpected error has occurred',
                    );
                }
            }
        }, [file, uploadItemName, onUpload, onHide]);

        /**
         * Resets the component state
         */
        const resetState = (): void => {
            setShowFileTypeError(false);
            setFile(null);
            setFileName('');
            setFileSize('');
            setProgress(0);
            setIsUploading(false);
            setShowFileSizeError(false);
            setError('');
        };

        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            accept: allowedFileTypes.reduce(
                (acc, ext) => {
                    const mimeType = MIME_TYPES[ext.toLowerCase()];
                    if (mimeType) {
                        acc[mimeType] = [`.${ext.toLowerCase()}`];
                    }
                    return acc;
                },
                {} as Record<string, string[]>,
            ),
            onDrop: handleFileUpload,
        });

        return (
            <Modal
                backdrop="opaque"
                isOpen={show}
                onOpenChange={(open) => setShow(open)}
                classNames={{
                    base: 'py-4 px-12 text-foreground',
                    backdrop: 'bg-foreground/20',
                }}
            >
                <ModalContent>
                    {() => (
                        <>
                            <ModalHeader className="flex flex-col gap-4">
                                <h3 className="text-center text-2xlarge font-bold">
                                    Upload {uploadItemName}
                                </h3>
                            </ModalHeader>

                            <ModalBody>
                                <div
                                    {...getRootProps({
                                        className: `border-1 rounded-large p-5 place-items-center cursor-pointer ${
                                            isDragActive ? 'border-primary' : 'border-content3'
                                        }`,
                                    })}
                                    data-testid="dropzone"
                                >
                                    <input {...getInputProps()} />
                                    <Icon
                                        icon="lucide:upload-cloud"
                                        className="h-14 w-14 text-foreground"
                                    />
                                    <p className="pb-1 text-small font-semibold text-foreground">
                                        Upload {uploadItemName}
                                    </p>
                                    <p className="text-mini text-foreground">
                                        Drag and drop here or{' '}
                                        <span className="text-primary underline">browse files</span>
                                    </p>
                                </div>

                                {/* Supported Formats and Max Size */}
                                <div className="mt-1 flex justify-between text-tiny text-foreground/50">
                                    <p>Supported formats: {allowedFileTypes.join(', ')}</p>
                                    <p>Maximum file size: {maxSize}MB</p>
                                </div>

                                {/* Selected File Info */}
                                {file && (
                                    <div className="mt-5 rounded-large border p-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex w-full items-center justify-start gap-2">
                                                <Icon
                                                    icon="lucide:file-spreadsheet"
                                                    width={20}
                                                    height={20}
                                                />
                                                <p
                                                    className="max-w-36 truncate text-mini font-medium text-foreground"
                                                    title={fileName}
                                                >
                                                    {fileName}
                                                </p>
                                            </div>
                                            <div className="flex w-full items-center justify-end gap-2">
                                                <p className="text-mini text-foreground">
                                                    {fileSize}
                                                </p>
                                                <Icon
                                                    icon="lucide:x"
                                                    width={20}
                                                    height={20}
                                                    className="border-border cursor-pointer rounded-full border p-1"
                                                    onClick={resetState}
                                                    aria-label="Remove selected file"
                                                    data-testid="remove-file-button"
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-3 flex items-baseline gap-2">
                                            <div className="h-1 w-full rounded-full bg-content3">
                                                <div
                                                    className="h-1 rounded-full bg-primary"
                                                    style={{ width: `${progress}%` }}
                                                ></div>
                                            </div>
                                            <p className="mt-1 text-right text-mini text-foreground">
                                                {progress}%
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Error Messages */}
                                {(showFileTypeError || showFileSizeError || error) && (
                                    <div className="mt-3 inline-grid rounded-large border border-danger-500 bg-danger-100 p-3 text-mini text-danger-700">
                                        <p className="flex items-center gap-1">
                                            We&apos;re sorry, the file {fileName ?? ''} could not be
                                            uploaded.
                                        </p>
                                        {showFileTypeError && <p>{ERROR_MESSAGES.FILE_TYPE}</p>}
                                        {showFileSizeError && <p>{ERROR_MESSAGES.FILE_SIZE}</p>}
                                        {error && <p>{error}</p>}
                                    </div>
                                )}
                            </ModalBody>

                            <ModalFooter>
                                <Button
                                    color="primary"
                                    variant="solid"
                                    isDisabled={!file || isUploading}
                                    onPress={handleSubmit}
                                    fullWidth={true}
                                    data-testid="upload-button"
                                >
                                    Upload {uploadItemName}
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        );
    },
);

UploadModal.displayName = 'UploadModal';
